import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Search, Star, Filter, ArrowLeft, User, Calendar, MessageSquare, Shield, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

export default function ProviderSearch() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [careGrades, setCareGrades] = useState([]);

  useEffect(() => {
    fetchProviders();
    fetchCareGrades();
  }, [minRating, availableOnly]);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchTerm) params.append('specialization', searchTerm);
      if (minRating > 0) params.append('min_rating', minRating);
      params.append('available_only', availableOnly);

      const response = await axios.get(`${API}/providers/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCareGrades = async () => {
    try {
      const response = await axios.get(`${API}/care-grades`);
      // Filter for G5-G6 only (identified therapy sessions)
      const therapyGrades = response.data.filter(g => ['G5', 'G6'].includes(g.code));
      setCareGrades(therapyGrades);
    } catch (error) {
      console.error('Failed to fetch care grades:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchProviders();
  };

  const ProviderCard = ({ provider }) => (
    <div 
      className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => setSelectedProvider(provider)}
      data-testid={`provider-card-${provider.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <User size={28} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-[#2D3748]">Dr. {provider.name}</h3>
              {provider.specialization && (
                <p className="text-sm text-primary">{provider.specialization}</p>
              )}
            </div>
            <ChevronRight size={20} className="text-[#A0AEC0] flex-shrink-0" />
          </div>
          
          <div className="flex items-center gap-4 mt-2 text-sm">
            {provider.average_rating && (
              <div className="flex items-center gap-1">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span className="text-[#2D3748] font-medium">{provider.average_rating}</span>
                {provider.total_reviews && (
                  <span className="text-[#718096]">({provider.total_reviews})</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1 text-[#718096]">
              <MessageSquare size={14} />
              <span>{provider.completed_sessions || 0} sessions</span>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            {provider.is_available ? (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                Available Now
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                Unavailable
              </span>
            )}
            {provider.certification && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                <Shield size={10} />
                {provider.certification}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const ProviderDetail = ({ provider }) => (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" data-testid="provider-detail-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center gap-3">
          <button onClick={() => setSelectedProvider(null)} className="p-1">
            <ArrowLeft size={24} className="text-[#718096]" />
          </button>
          <h2 className="text-lg font-semibold text-[#2D3748]">Provider Profile</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Header */}
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <User size={40} className="text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-[#2D3748]">Dr. {provider.name}</h3>
            {provider.specialization && (
              <p className="text-primary">{provider.specialization}</p>
            )}
            {provider.certification && (
              <div className="flex items-center justify-center gap-1 mt-2 text-sm text-[#718096]">
                <Shield size={14} className="text-blue-500" />
                {provider.certification}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-[#FAF9F6] rounded-xl">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
                <span className="font-semibold text-[#2D3748]">{provider.average_rating || '-'}</span>
              </div>
              <div className="text-xs text-[#718096]">Rating</div>
            </div>
            <div className="text-center p-3 bg-[#FAF9F6] rounded-xl">
              <div className="font-semibold text-[#2D3748] mb-1">{provider.completed_sessions || 0}</div>
              <div className="text-xs text-[#718096]">Sessions</div>
            </div>
            <div className="text-center p-3 bg-[#FAF9F6] rounded-xl">
              <div className="font-semibold text-[#2D3748] mb-1">{provider.total_reviews || 0}</div>
              <div className="text-xs text-[#718096]">Reviews</div>
            </div>
          </div>

          {/* Recent Feedback */}
          {provider.recent_feedback && provider.recent_feedback.length > 0 && (
            <div>
              <h4 className="font-medium text-[#2D3748] mb-3">Recent Feedback</h4>
              <div className="space-y-2">
                {provider.recent_feedback.map((fb, idx) => (
                  <div key={idx} className="p-3 bg-[#FAF9F6] rounded-xl">
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={12} className={i < fb.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                      ))}
                    </div>
                    {fb.comment && (
                      <p className="text-sm text-[#718096] italic">"{fb.comment}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Therapy Sessions (G5-G6) */}
          <div>
            <h4 className="font-medium text-[#2D3748] mb-3">Book a Therapy Session</h4>
            <div className="space-y-2">
              {careGrades.map((grade) => (
                <button
                  key={grade.code}
                  onClick={() => navigate(`/book-appointment?provider=${provider.id}&grade=${grade.code}`)}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl text-left hover:border-primary/50 hover:bg-primary/5 transition-all"
                  data-testid={`book-${grade.code}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-[#2D3748]">{grade.name}</div>
                      <div className="text-sm text-[#718096]">{grade.description}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className="flex items-center gap-1 text-[#718096]">
                          <Clock size={12} />
                          {grade.duration_minutes} min
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-primary">
                        ₹{grade.discount_price || grade.base_price}
                      </div>
                      {grade.discount_price && grade.discount_price < grade.base_price && (
                        <div className="text-xs text-[#A0AEC0] line-through">₹{grade.base_price}</div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Availability Note */}
          {!provider.is_available && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-800">
              This provider is currently unavailable. You can still book an appointment for a future date.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader title="Find a Therapist" />

      <div className="container mx-auto max-w-2xl px-4 py-6">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by specialization..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-primary"
              data-testid="provider-search-input"
            />
          </div>
        </form>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              showFilters ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-[#718096]'
            }`}
            data-testid="toggle-filters"
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            onClick={() => { setAvailableOnly(!availableOnly); setLoading(true); }}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              availableOnly ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-[#718096]'
            }`}
            data-testid="available-only-toggle"
          >
            Available Now
          </button>
        </div>

        {showFilters && (
          <div className="card p-4 mb-6">
            <div>
              <label className="text-sm text-[#718096] mb-2 block">Minimum Rating</label>
              <div className="flex gap-2">
                {[0, 3, 4, 4.5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => { setMinRating(rating); setLoading(true); }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors ${
                      minRating === rating ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-[#718096]'
                    }`}
                  >
                    {rating > 0 && <Star size={12} className="text-yellow-400 fill-yellow-400" />}
                    {rating === 0 ? 'Any' : `${rating}+`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-full bg-gray-200"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12">
            <User size={48} className="mx-auto mb-4 text-[#A0AEC0]" />
            <h3 className="font-medium text-[#2D3748] mb-2">No providers found</h3>
            <p className="text-sm text-[#718096]">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#718096]">{providers.length} therapist{providers.length !== 1 ? 's' : ''} found</p>
            {providers.map((provider) => (
              <ProviderCard key={provider.id} provider={provider} />
            ))}
          </div>
        )}
      </div>

      {/* Provider Detail Modal */}
      {selectedProvider && <ProviderDetail provider={selectedProvider} />}
    </div>
  );
}
